
import React, { Component } from "react";
import { Link } from "react-router-dom";

const ALL_BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const STATUS_BUCKET_MAP = {
  critical: "critical",
  low: "low",
  sufficient: "sufficient",
  available: "sufficient",
  good: "sufficient",
  ok: "sufficient",
  fresh: "sufficient",
  warning: "low",
  near_expiry: "low",
};

const EXCLUDED_STATUSES = ["discarded", "issued", "quarantine"];

const REGISTER_DONOR_PATH = "/register";

const COLORS = {
  bg: "#FAF7F3",
  ink: "#1B1B1F",
  inkMuted: "#6B6560",
  hairline: "#E7E0D8",
  card: "#FFFFFF",
  critical: "#8C1D1D",
  criticalBg: "#F6E3E3",
  low: "#B5730A",
  lowBg: "#F7EBD8",
  ok: "#2F6B4F",
  okBg: "#E4EFE9",
};

const BLOOD_FAMILY_THEME = {
  A: { border: "#312f311c", background: "#d3cfd3", accent: "#3b0228" },
  B: { border: "#1D4E8C", background: "#cbdcf0", accent: "#1D4E8C" },
  AB: { border: "#40035c", background: "#e7c8f5", accent: "#250135" },
  O: { border: "#09d3e6", background: "#c8eef1", accent: "#09d3e6" },
};

function getBloodFamily(group) {
  if (group.startsWith("AB")) return "AB";
  if (group.startsWith("A")) return "A";
  if (group.startsWith("B")) return "B";
  return "O";
}

function emptyBucket() {
  return {
    critical: 0,
    low: 0,
    sufficient: 0,
    total: 0,
    issued: 0,
  };
}

function getDominantStatus(item) {
  if (item.critical > 0) return "critical";
  if (item.low > 0) return "low";
  if (item.sufficient > 0) return "sufficient";
  return "empty";
}

function formatTime(date) {
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

class LiveDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bloodGroups: ALL_BLOOD_GROUPS.map((group) => ({ group, ...emptyBucket() })),
      maxBucketValue: 1,
      loading: true,
      refreshing: false,
      error: null,
      lastUpdated: null,
      todayCollected: 0,
      todayIssued: 0,
      stockThresholds: {},
    };
    this.fetchStockThresholds = this.fetchStockThresholds.bind(this);
    this.fetchStockSummary = this.fetchStockSummary.bind(this);
    this.handleManualRefresh = this.handleManualRefresh.bind(this);
  }

  async componentDidMount() {
    this.injectFonts();
    await this.fetchStockThresholds();
    this.fetchStockSummary();
    this.pollTimer = setInterval(this.fetchStockSummary, 15000);
  }

  componentWillUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  }

  injectFonts() {
    if (document.getElementById("ld-fonts")) return;
    const link = document.createElement("link");
    link.id = "ld-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Mono:wght@500;600&family=Inter:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }


async fetchStockThresholds() {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/stock`);
    const json = await res.json();
    const rows = (json && json.data) || [];

    const byGroup = {};
    rows.forEach((r) => {
      byGroup[r.blood_group] = {
        unitsAvailable: Number(r.units_available) || 0,
        criticalThreshold: Number(r.critical_threshold) || 0,
        lowThreshold: Number(r.low_threshold) || 0,
        apiStatus: r.status, // "Critical" | "Low stock" | "Sufficient"
      };
    });

    this.setState({ stockThresholds: byGroup });
  } catch (err) {
    this.setState({ stockThresholds: {} });
  }
}
getStockStatus(item) {
  const g = this.state.stockThresholds[item.group];
  if (!g) return { label: "—", urgent: false, none: true, shortfall: 0 };

  const shortfall = Math.max(0, g.lowThreshold - g.unitsAvailable);
  const status = String(g.apiStatus || "").toLowerCase();

  if (status === "critical") return { label: "Urgent Blood Required", urgent: true, none: false, shortfall };
  if (status === "low stock") return { label: "Blood Required", urgent: false, none: false, shortfall };
  return { label: "—", urgent: false, none: true, shortfall: 0 };
}

  isToday(dateStr) {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }

  handleManualRefresh() {
    if (this.state.loading || this.state.refreshing) return;
    this.setState({ refreshing: true }, this.fetchStockSummary);
  }

  getIssueBloodGroup(row) {
    return row.blood_group || row.donation_blood_group || row.bag_blood_group || null;
  }

  async fetchStockSummary() {
    try {
      const [donationsRes, issuesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blood-donations`),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blood-issues`),
      ]);

      const data = await donationsRes.json();
      const issuesData = await issuesRes.json().catch(() => null);

      if (!data.status && !data.data) {
        this.setState({
          loading: false,
          refreshing: false,
          error: data.message || "Failed to load stock summary.",
        });
        return;
      }

      const buckets = {};
      ALL_BLOOD_GROUPS.forEach((g) => {
        buckets[g] = emptyBucket();
      });

      let todayCollected = 0;

      (data.data || []).forEach((bag) => {
  if (this.isToday(bag.created_at || bag.donation_date)) {
    todayCollected += 1;
  }

  const rawStatus = String(bag.status || "").toLowerCase().trim();
  if (EXCLUDED_STATUSES.includes(rawStatus)) return;

  const group = bag.blood_group;
  if (!buckets[group]) return;

  buckets[group].total += 1;
});


ALL_BLOOD_GROUPS.forEach((group) => {
  const g = this.state.stockThresholds[group];
  const total = buckets[group].total;

  if (!g) {
    buckets[group].sufficient = total; // no threshold configured — treat as ok
    return;
  }

  const apiStatus = String(g.apiStatus || "").toLowerCase();
  if (apiStatus === "critical") {
    buckets[group].critical = total;
  } else if (apiStatus === "low stock") {
    buckets[group].low = total;
  } else {
    buckets[group].sufficient = total;
  }
});
      let todayIssued = 0;
      if (issuesData && (issuesData.status || issuesData.data)) {
        (issuesData.data || []).forEach((row) => {
          // FIX: same reasoning — prefer created_at, fall back to issue_date.
          if (this.isToday(row.created_at || row.issue_date)) {
            todayIssued += 1;
          }

          const group = this.getIssueBloodGroup(row);
          if (!group || !buckets[group]) return;
          buckets[group].issued += 1;
        });
      }

      const bloodGroups = ALL_BLOOD_GROUPS.map((group) => ({
        group,
        ...buckets[group],
      }));

      const maxBucketValue = Math.max(
        1,
        ...bloodGroups.map((g) => Math.max(g.critical, g.low, g.sufficient))
      );

      this.setState({
        bloodGroups,
        maxBucketValue,
        loading: false,
        refreshing: false,
        error: null,
        lastUpdated: new Date(),
        todayCollected,
        todayIssued,
      });
    } catch (err) {
      this.setState({
        loading: false,
        refreshing: false,
        error: "Unable to connect to the server.",
      });
    }
  }

  renderCardDonut(item) {
  const g = this.state.stockThresholds[item.group] || {};
  const criticalThreshold = g.criticalThreshold ?? 0;
  const lowThreshold = g.lowThreshold ?? 0;

  const segments = [
  {
    key: "critical",
    label: "Critical",
    sub: `< ${criticalThreshold} Days`,
    value: item.critical,
    color: COLORS.critical,
  },
  {
    key: "low",
    label: "Low",
    sub: `< ${lowThreshold} Days`,
    value: item.low,
    color: COLORS.low,
  },
  {
    key: "sufficient",
    label: "Sufficient",
    sub: `${lowThreshold}+ Days`,
    value: item.sufficient,
    color: COLORS.ok,
  },
];
    const size = 110;
    const strokeWidth = 14;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const total = item.total || 0;
    const safeTotal = total || 1;
    const center = size / 2;

    let cumulative = 0;
    const arcs = segments.map((seg) => {
      const dash = (seg.value / safeTotal) * circumference;
      const fraction = seg.value / safeTotal;
      const startFraction = cumulative / circumference;
      const midFraction = startFraction + fraction / 2;
      const midAngle = midFraction * 360 - 90;
      const midRad = (midAngle * Math.PI) / 180;
      const labelX = center + radius * Math.cos(midRad);
      const labelY = center + radius * Math.sin(midRad);

      const arc = {
        ...seg,
        dashArray: `${dash} ${circumference - dash}`,
        dashOffset: -cumulative,
        labelX,
        labelY,
      };
      cumulative += dash;
      return arc;
    });

    const isCritical = item.critical > 0;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, width: "100%" }}>
        <div className={isCritical ? "ld-pulse" : ""} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={center} cy={center} r={radius} fill="none" stroke="#00000014" strokeWidth={strokeWidth} />
            {total > 0 &&
              arcs.map((arc) =>
                arc.value > 0 ? (
                  <circle
                    key={arc.key}
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={arc.dashArray}
                    strokeDashoffset={arc.dashOffset}
                    strokeLinecap="butt"
                    transform={`rotate(-90 ${center} ${center})`}
                    style={{ transition: "stroke-dasharray .4s ease" }}
                  />
                ) : null
              )}
          </svg>

          {arcs.map((arc) =>
            arc.value > 0 ? (
              <div
                key={arc.key}
                style={{
                  position: "absolute",
                  left: arc.labelX,
                  top: arc.labelY,
                  transform: "translate(-50%, -50%)",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                  pointerEvents: "none",
                }}
              >
                {arc.value}
              </div>
            ) : null
          )}

          
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, width: "100%" }}>
          {segments.map((seg) => (
            <span
              key={seg.key}
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: seg.color,
              }}
            >
              {seg.sub} 
            </span>
          ))}
        </div>
      </div>
    );
  }

  renderOverallDonut() {
    const { bloodGroups, loading } = this.state;

    const totals = bloodGroups.reduce(
      (acc, g) => {
        acc.critical += g.critical;
        acc.low += g.low;
        acc.sufficient += g.sufficient;
        return acc;


      },
      { critical: 0, low: 0, sufficient: 0 }
    );
    const grandTotal = totals.critical + totals.low + totals.sufficient;

    const segments = [
      { key: "critical", label: "Critical", sub: "Near expiry", value: totals.critical, color: COLORS.critical },
      { key: "low", label: "Low", sub: "Getting close", value: totals.low, color: COLORS.low },
      { key: "sufficient", label: "Sufficient", sub: "Healthy stock", value: totals.sufficient, color: COLORS.ok },
    ];

    const size = 160;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const safeTotal = grandTotal || 1;

    let cumulative = 0;
    const arcs = segments.map((seg) => {
      const dash = (seg.value / safeTotal) * circumference;
      const arc = { ...seg, dashArray: `${dash} ${circumference - dash}`, dashOffset: -cumulative };
      cumulative += dash;
      return arc;
    });

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        <div style={{ position: "relative", width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F0ECE5" strokeWidth={strokeWidth} />
            {grandTotal > 0 &&
              arcs.map((arc) =>
                arc.value > 0 ? (
                  <circle
                    key={arc.key}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={arc.dashArray}
                    strokeDashoffset={arc.dashOffset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    style={{ transition: "stroke-dasharray .4s ease" }}
                  />
                ) : null
              )}
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontFamily: "'calibri', sans-serif", fontSize: 26, fontWeight: 700, color: COLORS.ink }}>
              {loading ? "—" : grandTotal}
            </div>
            <div style={{ fontSize: 11, color: COLORS.inkMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Total Bags
            </div>
          </div>
        </div>

        <div style={{ width: "100%", marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          {segments.map((seg) => (
            <div key={seg.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: seg.color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{seg.label}</div>
                <div style={{ fontSize: 11, color: COLORS.inkMuted }}>{seg.sub}</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: seg.color }}>
                {loading ? "—" : seg.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderTodayActivity() {
    const { todayCollected, todayIssued, loading } = this.state;

    return (
      <div style={{ width: "100%" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.inkMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
          Today's Activity
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: 13, color: COLORS.ink }}>Collected</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: COLORS.ok }}>
              {loading ? "—" : todayCollected}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: 13, color: COLORS.ink }}>Issued</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: COLORS.critical }}>
              {loading ? "—" : todayIssued}
            </span>
          </div>
        </div>
      </div>
    );
  }

 renderSummaryTable() {
    const { bloodGroups } = this.state;
    const displayValue = (val) => (val === 0 || val === null || val === undefined ? "–" : val);

    return (
      <div className="container-fluid px-0" style={{ maxWidth: 974, margin: "0 auto 30px" }}>
        <div className="row g-3">
          {/* Table column */}
          <div className="col-12 col-lg-8">
            <div
              className="h-100"
              style={{
                background: "#ffffffe1",
                border: `1px solid ${COLORS.hairline}`,
                borderRadius: 14,
                padding: "18px 20px",
                boxShadow: "0 1px 3px rgba(27,27,31,0.04)",
              }}
            >
              <div className="table-responsive">
                <table
                  className="table-borderless mb-0"
                  style={{ tableLayout: "fixed", fontFamily: "'calibri', sans-serif" }}
                >
                  <colgroup>
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "40%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        className="text-start"
                        style={{ padding: "12px 10px", borderBottom: `1px solid ${COLORS.hairline}`, fontSize: 14, color: COLORS.inkMuted, textTransform: "uppercase" }}
                      >
                        Blood Group
                      </th>
                      <th
                        className="text-center"
                        style={{ padding: "12px 10px", borderBottom: `1px solid ${COLORS.hairline}`, fontSize: 14, color: COLORS.ok, textTransform: "uppercase" }}
                      >
                        Stock Available
                      </th>
                      <th
                        className="text-center"
                        style={{ padding: "14px 10px", borderBottom: `1px solid ${COLORS.hairline}`, fontSize: 14, color: COLORS.critical, textTransform: "uppercase" }}
                      >
                        Required
                      </th>
                      <th
                        className="text-start"
                        style={{ padding: "12px 10px", borderBottom: `1px solid ${COLORS.hairline}`, fontSize: 14, color: "#0d6efd", textTransform: "uppercase" }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bloodGroups.map((item) => {
                      const status = this.getStockStatus(item);
                      const requiredValue = status.none ? 0 : status.shortfall;

                      return (
                        <tr key={item.group}>
                          <td style={{ padding: "12px 10px", fontWeight: 700, fontSize: 16, color: COLORS.ink, borderBottom: `1px solid ${COLORS.hairline}` }}>
                            {item.group}
                          </td>
                          <td className="text-center" style={{ padding: "12px 10px", fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${COLORS.hairline}` }}>
                            {displayValue(item.total)}
                          </td>
                          <td className="text-center" style={{ padding: "12px 10px", fontSize: 16, color: COLORS.critical, fontWeight: 600, borderBottom: `1px solid ${COLORS.hairline}` }}>
                            {displayValue(requiredValue)}
                          </td>
                          <td style={{ padding: "12px 10px", borderBottom: `1px solid ${COLORS.hairline}`, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.25 }}>
                            {status.none ? (
                              <span style={{ fontSize: 14, color: COLORS.inkMuted }}>—</span>
                            ) : (
                              <Link
                                to={REGISTER_DONOR_PATH}
                                className={status.urgent ? "ld-blink" : ""}
                                style={{ display: "inline-block", fontSize: 13, fontWeight: 700, color: COLORS.critical, textDecoration: "underline", cursor: "pointer" }}
                              >
                                {status.label}
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Side cards column */}
          <div className="col-12 col-lg-4">
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-1 g-3">
              <div className="col">
                <div
                  className="h-100"
                  style={{ background: "#ffffffe1", border: `1px solid ${COLORS.hairline}`, borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(27,27,31,0.04)" }}
                >
                  {this.renderOverallDonut()}
                </div>
              </div>
              <div className="col">
                <div
                  className="h-100"
                  style={{ background: "#ffffffe1", border: `1px solid ${COLORS.hairline}`, borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 3px rgba(27,27,31,0.04)" }}
                >
                  {this.renderTodayActivity()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { bloodGroups, loading, refreshing, error, maxBucketValue, lastUpdated } = this.state;

    const totals = bloodGroups.reduce(
      (acc, g) => {
        acc.total += g.total;
        acc.critical += g.critical;
        acc.low += g.low;
        return acc;
      },
      { total: 0, critical: 0, low: 0 }
    );

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(180deg,#4d0000 0%, #4b0303 50%, #640303 100%)",
          overflow: "hidden",
          position: "relative",
          backgroundAttachment: "fixed",
          fontFamily: "'calibri', system-ui, sans-serif",
          padding: "80px 20px 20px",
        }}
      >
        <style>{`
        @font-face {
    font-family: 'Norwester';
    src: url('/fonts/norwester.woff2') format('woff2'),
         url('/fonts/norwester.woff') format('woff');
    font-weight: normal;
    font-display: swap;
  }
        .awt-heartbeat-path {
  stroke-dasharray: 340;
  stroke-dashoffset: 680;
  animation: awtDraw 4.5s ease-in-out infinite;
}

@keyframes awtDraw {
  0% {
    stroke-dashoffset: 680;
    opacity: .35;
  }
  45% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  55% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  100% {
    stroke-dashoffset: -680;
    opacity: .35;
  }
}
        @keyframes bloodDrop {
  0% {
    transform: translateY(-120px) scale(0.7);
    opacity: 0;
  }

  15% {
    opacity: .6;
  }

  100% {
    transform: translateY(120vh) scale(1);
    opacity: 0;
  }
}

@keyframes sway {
  0% {
    margin-left: 0;
  }

  50% {
    margin-left: 15px;
  }

  100% {
    margin-left: -10px;
  }
}

.ld-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.ld-drop {
  position: absolute;
  top: -80px;
  width: 18px;
  height: 24px;
  background: rgba(255,40,40,.18);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  transform: rotate(45deg);

  animation:
      bloodDrop linear infinite,
      sway ease-in-out infinite;
  filter: blur(.4px);
}
          @keyframes ldPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
          }
          @keyframes ldSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes ldBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.15; }
          }
          .ld-pulse { animation: ldPulse 1.8s ease-in-out infinite; }
          .ld-blink { animation: ldBlink 1s step-start infinite; }
          .ld-card {
            transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
          }
          .ld-refresh-icon.spinning { animation: ldSpin 0.9s linear infinite; }
          .ld-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            justify-content: center;
            gap: 18px;
            width: 100%;
            max-width: 974px;
            margin: 0 auto;
          }
          @media (max-width: 1180px) {
            .ld-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          }
          @media (max-width: 820px) {
            .ld-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 480px) {
            .ld-grid { grid-template-columns: minmax(0, 1fr); }
          }
          @media (prefers-reduced-motion: reduce) {
            .ld-pulse, .ld-refresh-icon.spinning, .ld-blink { animation: none; }
          }
        `}</style>

        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div
            style={{
              margin: 50,
              paddingBottom: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div>

              <h1
                style={{
                  fontFamily: "'calibri', serif",
                  fontSize: 34,
                  fontWeight: 600,
                  color: "#fff",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Blood Units — Real Time Stock
              </h1>
              <div
                style={{
                  fontFamily: "'calibri', serif",
                  fontSize: 16,
                  letterSpacing: "0.14em",
                  color: "#fff",
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >

                Prepared, Not Panicked
              </div>
            </div>
            <svg
              width="100%"
              height="30"
              viewBox="0 0 1200 30"
              preserveAspectRatio="none"
              style={{
                display: "block",
                marginBottom: 24,
              }}
            >
              <line
                x1="0"
                y1="15"
                x2="1200"
                y2="15"
                stroke="rgba(255,255,255,.18)"
                strokeWidth="1"
              />

              <path
                className="awt-heartbeat-path"
                d="M0,15
       L470,15
       L495,15
       L508,4
       L522,26
       L536,15
       L560,15
       L575,8
       L588,15
       L1200,15"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {this.renderSummaryTable()}
          {error && (
            <div
              style={{
                background: "#FBEAEA",
                color: COLORS.critical,
                border: `1px solid ${COLORS.critical}33`,
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: 14,
                marginBottom: 24,
              }}
            >
              {error}
            </div>
          )}

          <div className="ld-grid">
            {bloodGroups.map((item) => {
              const dominant = loading ? "empty" : getDominantStatus(item);
              const family = getBloodFamily(item.group);
              const theme = BLOOD_FAMILY_THEME[family];

              return (
                <div
                  key={item.group}
                  className="ld-card"
                  style={{
                    background: theme.background,
                    border: `1px solid ${theme.border}55`,
                    borderTop: `4px solid ${theme.accent}`,
                    borderRadius: 14,
                    padding: "12px 14px",
                    boxShadow:
                      dominant === "critical"
                        ? `0 0 0 1px ${COLORS.critical}22, 0 4px 14px ${COLORS.critical}22`
                        : "0 1px 3px rgba(27,27,31,0.04)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontFamily: "'calibri', serif", fontWeight: 700, fontSize: 24, color: COLORS.ink, lineHeight: 1 }}>
                        {item.group}
                      </div>
                    </div>
                    <div style={{ fontFamily: "'calibri', sans-serif", fontSize: 24, fontWeight: 600, color: COLORS.ink }}>
                      {loading ? "—" : item.total}
                      <span style={{ fontSize: 11, color: COLORS.inkMuted, fontFamily: "'calibri', sans-serif", marginLeft: 4, fontWeight: 500 }}>
                        Units
                      </span>
                    </div>
                  </div>

                  {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                      {[0, 1, 2].map((i) => (
                        <div key={i} style={{ width: 32, height: 80, borderRadius: 6, background: "#F0ECE5" }} />
                      ))}
                    </div>
                  ) : (
                    this.renderCardDonut(item, maxBucketValue)
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}

export default LiveDashboard;